import React, {
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  IonAccordion,
  IonAccordionGroup,
  IonButton,
  IonButtons,
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardSubtitle,
  IonCardTitle,
  IonCol,
  IonContent,
  IonFooter,
  IonGrid,
  IonHeader,
  IonIcon,
  IonImg,
  IonInput,
  IonItem,
  IonLabel,
  IonList,
  IonLoading,
  IonModal,
  IonPage,
  IonRow,
  IonText,
  IonTextarea,
  IonTitle,
  IonToolbar,
  useIonToast,
} from "@ionic/react";
import { useHistory, useParams } from "react-router-dom";
import {
  cubeOutline,
  searchOutline,
  searchSharp,
  timerOutline,
} from "ionicons/icons";
import { withAITracking } from "@microsoft/applicationinsights-react-js";
import { reactPlugin } from "../util/AppInsights";
import PageHeader from "../components/PageHeader";
import BuyerContactField from "../components/BuyerContactField";
import POApprovalLogActivity from "../components/POApprovalLogActivity";
import FilterModal from "../components/FilterModal";
import ISSPopover from "../components/ISSPopover";
import { TranslationsContext } from "../util/Translations";
import {
  PurchaseOrderApprovalAccounting,
  PurchaseOrderApprovalHeader,
  PurchaseOrderApprovalLine,
  WorkOrderActivity,
} from "../models/workorders/WorkOrder";
import { PlantCurrency } from "../models/plant/Plant";
import {
  ProcessPurchaseOrderData,
  getPurchaseOrderActivityLog,
  getPurchaseOrderHeader,
  getPurchaseOrderItemImage,
  getPurchaseOrderLines,
  getReviewers,
  processPurchaseOrders,
} from "../api/MobileApproval";
import { getOptionByIdWithCORP } from "../api/TabwareOptions";
import { getValueSearchDetails } from "../api/QueryDesigner";
import { findEmployee } from "../api/Employees";
import { getCurrency } from "../util/PlantHelper";
import { formatCurrencyUICurrency } from "../util/UIHelper";
import { getValidValueDisplayText } from "../util/DropdownDisplayModeHelper";
import { getCurrentPlant } from "../util/ApiOptionsHelper";
import { getUserId } from "../util/UserHelper";
import { ApiResponse } from "../models/api/ApiResponse";
import "../components/WOApprovalModal.css";

type ApprovalAction = "approve" | "review" | "reject";

// US 903160 - Mobile PO Approvals part 2. Full PO approval detail screen reached
// from the approval queue (route /POApproval/:poNumber). Shows the PO header,
// buyer contact, activity log, all lines (expand/collapse) with spec + accounting
// detail, and a shared Review/Reject/Approve action sheet.
const POApprovalDetail: React.FC = () => {
  const { poNumber: poNumberParam } = useParams<{ poNumber: string }>();
  const poNumber = decodeURIComponent(poNumberParam || "");
  const history = useHistory();
  const { translations } = useContext(TranslationsContext);
  const [presentToast] = useIonToast();

  const [showLoading, setShowLoading] = useState(false);
  const [header, setHeader] = useState<PurchaseOrderApprovalHeader>();
  const [lines, setLines] = useState<PurchaseOrderApprovalLine[]>([]);
  const [activity, setActivity] = useState<WorkOrderActivity[]>([]);
  const [currency, setCurrency] = useState<PlantCurrency>();
  const [dropdownMode, setDropdownMode] = useState<string>("");
  const [useLineDiscounts, setUseLineDiscounts] = useState(false);
  const [showLog, setShowLog] = useState(false);

  // expand/collapse state, keyed by line number
  const [openMap, setOpenMap] = useState<{ [key: string]: boolean }>({});
  const [thumbnails, setThumbnails] = useState<{ [item: string]: string }>({});

  // action sheet
  const [sheetAction, setSheetAction] = useState<ApprovalAction | null>(null);
  const [comments, setComments] = useState<string>("");
  const [nextReviewer, setNextReviewer] = useState<string>("");

  // employee type-ahead (next reviewer picker)
  const popover = useRef<HTMLIonPopoverElement>(null);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [searchResults, setSearchResults] = useState<string[]>([]);
  const [searchValue, setSearchValue] = useState("");
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [filterModalModuleName, setFilterModalModuleName] = useState("");

  const money = (value: number | undefined) =>
    formatCurrencyUICurrency(value, currency);

  const codeDisplay = (code?: string, description?: string) =>
    getValidValueDisplayText({ Code: code, Description: description }, dropdownMode);

  const loadDetail = async () => {
    setShowLoading(true);
    try {
      const [
        headerRes,
        linesRes,
        activityRes,
        reviewerRes,
        dropdownOpt,
        discountOpt,
      ] = await Promise.all([
        getPurchaseOrderHeader(poNumber),
        getPurchaseOrderLines(poNumber),
        getPurchaseOrderActivityLog(poNumber),
        getReviewers({
          plant: await getCurrentPlant(),
          user: await getUserId(),
          type: 3,
        }),
        getOptionByIdWithCORP("MobileDropDownViewSettings"),
        getOptionByIdWithCORP("UseLineDiscounts"),
      ]);

      setHeader(headerRes.data?.Data);
      setLines(linesRes.data?.Data ?? []);
      setActivity(activityRes.data?.Data ?? []);
      setNextReviewer(reviewerRes.data?.DefaultReviewer ?? "");
      setDropdownMode(
        dropdownOpt.data?.PlantValue || dropdownOpt.data?.DefaultValue || ""
      );
      const disc =
        discountOpt.data?.PlantValue || discountOpt.data?.DefaultValue || "No";
      setUseLineDiscounts(String(disc).toLowerCase() === "yes");
      setCurrency(await getCurrency());
    } finally {
      setShowLoading(false);
    }
  };

  useEffect(() => {
    loadDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [poNumber]);

  const loadThumbnail = async (item: string) => {
    if (!item || thumbnails[item] !== undefined) return;
    // mark as attempted (empty string) to avoid repeat fetches
    setThumbnails((prev) => ({ ...prev, [item]: "" }));
    const res = await getPurchaseOrderItemImage(item);
    const url = res.data?.Image;
    if (url) {
      setThumbnails((prev) => ({ ...prev, [item]: url }));
    }
  };

  const allExpanded =
    lines.length > 0 && lines.every((l) => openMap[String(l.LineNum)]);

  const setAllExpanded = (expand: boolean) => {
    const next: { [key: string]: boolean } = {};
    lines.forEach((l) => (next[String(l.LineNum)] = expand));
    setOpenMap(next);
    if (expand) lines.forEach((l) => loadThumbnail(l.Item));
  };

  // --- next-reviewer employee type-ahead ---
  const handleEmployeeSearch = (e: CustomEvent) => {
    const newVal = (e.target as HTMLInputElement)?.value ?? "";
    setNextReviewer(newVal);
    if (newVal.length > 2) {
      findEmployee(newVal).then((response) => {
        if (response.status === 200) {
          if (Object.keys(response.data).length !== 0) {
            popover.current!.event = e;
            setPopoverOpen(true);
          } else if (popoverOpen) {
            setPopoverOpen(false);
          }
          const ids = Object.entries(response.data).map(([id]) => id);
          setSearchResults(ids);
        }
      });
    } else {
      if (popoverOpen) setPopoverOpen(false);
      if (searchResults.length > 0) setSearchResults([]);
    }
  };

  const openReviewerValueSearch = async () => {
    const response: ApiResponse = await getValueSearchDetails({
      tableName: "em_employee",
      columnName: "employee_id",
    });
    if (response.status === 200) {
      setFilterModalModuleName(response.data.QueryWizardModuleName);
      setIsFilterModalOpen(true);
    }
  };

  const openSheet = (action: ApprovalAction) => {
    setComments("");
    setSheetAction(action);
  };

  const confirmAction = async () => {
    if (!sheetAction) return;
    setShowLoading(true);
    const data: ProcessPurchaseOrderData = {
      Action: sheetAction,
      Comments: comments,
      NextReviewer: nextReviewer,
      PurchaseOrder: poNumber,
    };
    try {
      const response = await processPurchaseOrders([data]);
      const message =
        response.data?.PoInfoResult?.[0]?.MessageList?.[0]?.Text ??
        (translations["lbl_action_complete"] || "Action complete.");
      presentToast({ message, duration: 3000, position: "top" });
      setSheetAction(null);
      history.replace("/POApproval");
    } finally {
      setShowLoading(false);
    }
  };

  const sheetTitle =
    sheetAction === "approve"
      ? translations["lbl_approve"] || "Approve"
      : sheetAction === "review"
      ? translations["lbl_review"] || "Review"
      : translations["lbl_reject"] || "Reject";

  const sheetContext =
    sheetAction === "approve"
      ? `${translations["lbl_approve"] || "Approve"} · ${money(header?.Total)}`
      : sheetAction === "reject"
      ? translations["lbl_po_reject_context"] ||
        "Select the reviewer to return this PO to."
      : translations["lbl_po_review_context"] ||
        "Select the reviewer to send this PO to.";

  return (
    <IonPage>
      <IonLoading
        isOpen={showLoading}
        onDidDismiss={() => setShowLoading(false)}
        duration={8000}
      />
      <PageHeader
        title={translations["lbl_mob_po_approval_title"] || "Approval"}
        showBack
        altBackUrl="/POApproval"
      ></PageHeader>
      <IonContent>
        {/* PO header summary card */}
        <IonCard>
          <IonCardHeader>
            <IonCardTitle>{header?.PoNum || poNumber}</IonCardTitle>
            <IonCardSubtitle>
              {header?.SupplierName
                ? `${header.SupplierName}${
                    header.Supplier ? ` (${header.Supplier})` : ""
                  }`
                : header?.Supplier}
            </IonCardSubtitle>
          </IonCardHeader>
          <IonCardContent className="ion-no-padding">
            {!!header?.Buyer && (
              <BuyerContactField
                label={translations["lbl_po_buyer"] || "Buyer"}
                code={header.Buyer}
                name={header.BuyerName || header.Buyer}
                phone={header.BuyerPhone}
                fax={header.BuyerFax}
              />
            )}
            <IonItem
              button
              detail={false}
              disabled={activity.length === 0}
              onClick={() => setShowLog(true)}
            >
              <IonIcon slot="start" icon={timerOutline} />
              <IonLabel>
                {translations["lbl_log_activity"] || "Log Activity"}
              </IonLabel>
            </IonItem>
          </IonCardContent>
        </IonCard>

        {/* Line list controls */}
        <IonItem lines="none">
          <IonLabel>
            {translations["lbl_po_line_items"] || "Line Items"} · {lines.length}
          </IonLabel>
          <IonButton
            fill="clear"
            slot="end"
            onClick={() => setAllExpanded(!allExpanded)}
          >
            {allExpanded
              ? translations["lbl_btn_collapse_all"] || "Collapse all"
              : translations["lbl_btn_expand_all"] || "Expand all"}
          </IonButton>
        </IonItem>

        {/* Line items */}
        {lines.map((line) => {
          const key = String(line.LineNum);
          const isOpen = !!openMap[key];
          const thumb = thumbnails[line.Item];
          return (
            <IonCard key={key}>
              <IonAccordionGroup
                value={isOpen ? key : undefined}
                onIonChange={(e) => {
                  const opened = (e.detail as any).value === key;
                  setOpenMap((prev) => ({ ...prev, [key]: opened }));
                  if (opened) loadThumbnail(line.Item);
                }}
              >
                <IonAccordion value={key}>
                  <IonItem slot="header">
                    {thumb ? (
                      <IonImg
                        slot="start"
                        src={thumb}
                        style={{ width: 44, height: 44, objectFit: "contain" }}
                      />
                    ) : (
                      <IonIcon slot="start" icon={cubeOutline} size="large" />
                    )}
                    <IonLabel class="ion-text-wrap">
                      <h3>{line.ItemDesc}</h3>
                      <p>
                        {line.Qty} {line.UOM} × {money(line.UnitCost)}
                      </p>
                      <p>
                        {(translations["lbl_popurchaseorderline_item"] ||
                          "Item")}
                        : {line.Item}
                      </p>
                    </IonLabel>
                    <IonText slot="end">{money(line.ExtCost)}</IonText>
                  </IonItem>

                  <div slot="content">
                    {/* Specification */}
                    <IonList>
                      <IonItem lines="none">
                        <IonLabel class="ion-text-wrap">
                          <p>
                            {translations["lbl_po_extended_description"] ||
                              "Extended Description"}
                          </p>
                          <h3 className="ion-text-wrap">
                            {line.ExtendedDesc || line.ItemDesc}
                          </h3>
                        </IonLabel>
                      </IonItem>
                      <SpecRow
                        label={translations["lbl_po_manufacturer"] || "Manufacturer"}
                        value={line.Manufacturer}
                      />
                      <SpecRow
                        label={
                          translations["lbl_po_manufacturer_part_no"] ||
                          "Manufacturer Part No."
                        }
                        value={line.ManufacturerPartNo}
                      />
                      <SpecRow
                        label={
                          translations["lbl_popurchaseorderline_quantity_ordered"] ||
                          "Quantity"
                        }
                        value={`${line.Qty} ${line.UOM || ""}`.trim()}
                      />
                      <SpecRow
                        label={
                          translations["lbl_popurchaseorderline_unit_cost"] ||
                          "Unit Cost"
                        }
                        value={money(line.UnitCost)}
                      />
                      {useLineDiscounts && (
                        <SpecRow
                          label={translations["lbl_po_discount_amount"] || "Discount"}
                          value={money(line.DiscountAmount)}
                        />
                      )}
                      <SpecRow
                        label={
                          translations["lbl_popurchaseorderline_extended_cost"] ||
                          "Extended Cost"
                        }
                        value={money(line.ExtCost)}
                      />
                    </IonList>

                    {/* Accounting */}
                    {!!line.Accounting && line.Accounting.length > 0 && (
                      <>
                        <IonItem lines="none">
                          <IonLabel color="medium">
                            {line.Accounting.length > 1
                              ? `${
                                  translations["lbl_po_accounting"] || "Accounting"
                                } · ${
                                  translations["lbl_po_split_across"] ||
                                  "Split across"
                                } ${line.Accounting.length}`
                              : translations["lbl_po_accounting"] || "Accounting"}
                          </IonLabel>
                        </IonItem>
                        {line.Accounting.map((a) => (
                          <AccountingBlock
                            key={a.Counter}
                            alloc={a}
                            money={money}
                            codeDisplay={codeDisplay}
                            taxCode={line.TaxCode}
                            taxCodeDesc={line.TaxCodeDesc}
                            translations={translations}
                          />
                        ))}
                      </>
                    )}
                  </div>
                </IonAccordion>
              </IonAccordionGroup>
            </IonCard>
          );
        })}
      </IonContent>

      {/* Action bar */}
      <IonFooter>
        <IonToolbar>
          <IonButtons slot="start">
            <IonButton fill="outline" onClick={() => openSheet("review")}>
              {translations["lbl_review"] || "Review"}
            </IonButton>
            <IonButton
              fill="outline"
              color="danger"
              onClick={() => openSheet("reject")}
            >
              {translations["lbl_reject"] || "Reject"}
            </IonButton>
          </IonButtons>
          <IonButtons slot="end">
            <IonButton fill="solid" color="primary" onClick={() => openSheet("approve")}>
              {(translations["lbl_approve"] || "Approve")}
              {header ? ` · ${money(header.Total)}` : ""}
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonFooter>

      {/* Activity log */}
      <POApprovalLogActivity
        isOpen={showLog}
        dismiss={() => setShowLog(false)}
        setShowLoading={setShowLoading}
        poActivity={activity}
      />

      {/* Shared Review / Reject / Approve action sheet */}
      <IonModal
        isOpen={sheetAction !== null}
        onDidDismiss={() => setSheetAction(null)}
        initialBreakpoint={0.6}
        breakpoints={[0, 0.6, 0.9]}
      >
        <FilterModal
          isOpen={isFilterModalOpen}
          moduleName={filterModalModuleName}
          cancel={() => setIsFilterModalOpen(false)}
          confirm={(selectedItem: string) => {
            setNextReviewer(selectedItem);
            setIsFilterModalOpen(false);
          }}
        />
        <IonHeader>
          <IonToolbar>
            <IonTitle>{sheetTitle}</IonTitle>
            <IonButtons slot="end">
              <IonButton onClick={() => setSheetAction(null)}>
                {translations["lbl_btn_cancel"] || "Cancel"}
              </IonButton>
            </IonButtons>
          </IonToolbar>
        </IonHeader>
        <IonContent className="ion-padding">
          <IonText>
            <p>{sheetContext}</p>
          </IonText>
          <IonItem>
            <IonLabel>
              {translations["lbl_popurchaseorder_nextreviewer"] || "Next Reviewer"}
            </IonLabel>
            <IonInput
              class="ion-text-end"
              value={nextReviewer}
              placeholder={
                translations["lbl_po_final_approval"] || "None — final approval"
              }
              onIonInput={handleEmployeeSearch}
            ></IonInput>
            <IonIcon
              slot="end"
              ios={searchOutline}
              md={searchSharp}
              onClick={openReviewerValueSearch}
            ></IonIcon>
          </IonItem>
          <IonItem>
            <IonLabel position="stacked">
              {translations["lbl_incyclecountbatch_comment"] || "Comment"}
            </IonLabel>
            <IonTextarea
              autoGrow={true}
              placeholder={translations["lbl_enter comments"] || "Enter Comments"}
              value={comments}
              onIonChange={(ev) => setComments(String(ev.detail.value ?? ""))}
            ></IonTextarea>
          </IonItem>
          <IonButton expand="block" onClick={confirmAction}>
            {sheetAction === "approve"
              ? `${translations["lbl_approve"] || "Approve"} · ${money(
                  header?.Total
                )}`
              : sheetAction === "reject"
              ? translations["lbl_po_reject_confirm"] || "Reject PO"
              : translations["lbl_po_review_confirm"] || "Send for review"}
          </IonButton>
          <IonButton
            expand="block"
            fill="outline"
            onClick={() => setSheetAction(null)}
          >
            {translations["lbl_btn_cancel"] || "Cancel"}
          </IonButton>
        </IonContent>
      </IonModal>

      <ISSPopover
        popover={popover}
        popoverOpen={popoverOpen}
        setPopoverOpen={setPopoverOpen}
        searchResults={searchResults}
        setSearchResults={setSearchResults}
        searchValue={searchValue}
        setSearchValue={setSearchValue}
        setElementValue={setNextReviewer}
      />
    </IonPage>
  );
};

const SpecRow: React.FC<{ label: string; value?: string }> = ({
  label,
  value,
}) => {
  if (!value) return null;
  return (
    <IonItem>
      <IonLabel class="ion-text-wrap">
        <p>{label}</p>
        <h3>{value}</h3>
      </IonLabel>
    </IonItem>
  );
};

const AccountingBlock: React.FC<{
  alloc: PurchaseOrderApprovalAccounting;
  money: (v: number | undefined) => string;
  codeDisplay: (code?: string, description?: string) => string;
  taxCode?: string;
  taxCodeDesc?: string;
  translations: { [key: string]: string };
}> = ({ alloc, money, codeDisplay, taxCode, taxCodeDesc, translations }) => {
  return (
    <IonCard>
      <IonCardContent>
        <IonGrid>
          <AcctRow
            label={translations["lbl_po_percentage"] || "Percentage"}
            value={`${alloc.Percent}%`}
          />
          <AcctRow
            label={translations["lbl_po_amount"] || "Amount"}
            value={money(alloc.Amount)}
          />
          <AcctRow
            label={translations["lbl_po_account"] || "Account"}
            value={codeDisplay(alloc.Account, alloc.AccountDesc)}
          />
          <AcctRow
            label={translations["lbl_po_area"] || "Area"}
            value={codeDisplay(alloc.Area, alloc.AreaDesc)}
          />
          <AcctRow
            label={translations["lbl_po_department"] || "Department"}
            value={codeDisplay(alloc.Department, alloc.DepartmentDesc)}
          />
          <AcctRow
            label={translations["lbl_po_project"] || "Project"}
            value={codeDisplay(alloc.Project, alloc.ProjectDesc)}
          />
          <AcctRow
            label={translations["lbl_po_work_order"] || "Work Order"}
            value={codeDisplay(alloc.WorkOrder, alloc.WorkOrderDesc)}
          />
          <AcctRow
            label={translations["lbl_po_tax_code"] || "Tax Code"}
            value={codeDisplay(taxCode, taxCodeDesc)}
          />
        </IonGrid>
      </IonCardContent>
    </IonCard>
  );
};

const AcctRow: React.FC<{ label: string; value?: string }> = ({
  label,
  value,
}) => {
  if (!value) return null;
  return (
    <IonRow>
      <IonCol>{label}</IonCol>
      <IonCol class="ion-text-end">{value}</IonCol>
    </IonRow>
  );
};

export default withAITracking(reactPlugin, POApprovalDetail, "POApprovalDetail");
